// src/Pages/OnboardingPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgeVerification, type AgeRestrictions } from '@/components/AgeVerification';
import { TermsOfService } from '@/components/TermsOfService';

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'age' | 'terms' | 'complete'>('age');
  const [userId, setUserId] = useState<string>('');

  useEffect(() => {
    // Get user ID from localStorage
    const userStr = localStorage.getItem('whipt_user');
    if (userStr) {
      const user = JSON.parse(userStr);
      setUserId(user.id);
    }

    // Check if onboarding needed
    const needsOnboarding = localStorage.getItem('whipt_needs_onboarding');
    if (needsOnboarding !== 'true') {
      // User doesn't need onboarding, redirect to organizer
      navigate('/organizer');
      return;
    }

    // Check if user has already completed parts of onboarding
    const ageAlreadyVerified = localStorage.getItem('whipt_age_verification');
    const termsAlreadyAccepted = localStorage.getItem('whipt_terms_accepted') === 'true';
    
    if (ageAlreadyVerified && termsAlreadyAccepted) {
      // Already completed - shouldn't be here, redirect
      console.log('✅ Already completed onboarding, redirecting');
      localStorage.removeItem('whipt_needs_onboarding');
      navigate('/organizer');
    } else if (ageAlreadyVerified) {
      // Age done, skip to terms
      console.log('ℹ️ Age already verified, skipping to terms');
      setStep('terms');
    }
    // Otherwise stay on 'age' step (default)
  }, [navigate]);

  const handleAgeVerified = async (restrictions: AgeRestrictions) => {
    if (!restrictions.accountActive) {
      alert('Your account is pending parental consent. You\'ll receive an email when it\'s activated.');
      navigate('/organizer/sign-in');
      return;
    }

    // Update database - age verified
    try {
      const { db } = await import('@/database/Database');
      await db.run(
        'UPDATE users SET has_verified_age = 1 WHERE id = ?',
        [userId]
      );
      console.log('✅ Age verification flag updated');
    } catch (err) {
      console.error('Failed to update age verification flag:', err);
    }

    // Check if terms already accepted (returning user)
    const termsAlreadyAccepted = localStorage.getItem('whipt_terms_accepted') === 'true';
    
    if (termsAlreadyAccepted) {
      console.log('✅ Terms already accepted, skipping TOS panel');
      // Mark onboarding complete and redirect
      try {
        const { db } = await import('@/database/Database');
        await db.run(
          'UPDATE users SET has_accepted_beta_terms = 1, has_completed_onboarding = 1 WHERE id = ?',
          [userId]
        );
      } catch (err) {
        console.error('Failed to update onboarding flags:', err);
      }
      localStorage.removeItem('whipt_needs_onboarding');
      localStorage.setItem('whipt_organizer_mode', 'student');
      window.location.href = '/organizer';
    } else {
      console.log('ℹ️ First time user, showing TOS panel');
      setStep('terms');
    }
  };

  const handleTermsAccepted = async () => {
    // Update database - terms accepted and onboarding complete
    try {
      const { db } = await import('@/database/Database');
      await db.run(
        'UPDATE users SET has_accepted_beta_terms = 1, has_completed_onboarding = 1 WHERE id = ?',
        [userId]
      );
      console.log('✅ Terms accepted and onboarding completed');
    } catch (err) {
      console.error('Failed to update terms/onboarding flags:', err);
    }

    // Clear onboarding flag and navigate to organizer
    localStorage.removeItem('whipt_needs_onboarding');
    localStorage.setItem('whipt_organizer_mode', 'student');
    setStep('complete');
    window.location.href = '/organizer';
  };

  const handleTermsDeclined = () => {
    // User declined terms - log them out
    localStorage.clear();
    navigate('/organizer/sign-in');
  };

  if (step === 'age') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#05020b] via-[#0a0513] to-[#0f0820]">
        <AgeVerification 
          onVerified={handleAgeVerified}
          onClose={() => {
            // Allow closing and move to terms
            setStep('terms');
          }}
        />
      </div>
    );
  }

  if (step === 'terms') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#05020b] via-[#0a0513] to-[#0f0820]">
        <TermsOfService 
          onAccept={handleTermsAccepted}
          onDecline={handleTermsDeclined}
        />
      </div>
    );
  }

  // Complete - showing loading while redirecting
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#05020b] via-[#0a0513] to-[#0f0820] flex items-center justify-center">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-400 mb-4"></div>
        <p className="text-slate-400">Setting up your organizer...</p>
      </div>
    </div>
  );
}
